import type { ProductCardBadge } from './types';

/**
 * The two catalogue flags a badge is drawn from, as any service carries them.
 *
 * Both are optional and nullable because the services disagree about how they arrive:
 * UPS types them nullable, and the listings API omits them from products it has not
 * scored. Neither absence badges a product.
 *
 * `starBrand` keeps the catalogue's own name rather than being renamed to match the
 * customer-facing label. There is no `topBrand` attribute anywhere upstream, and a
 * reader looking for one is what this name stops.
 */
export interface ProductCardBadgeAttributes {
  /** "Most Loved" in customer-facing copy. */
  highlyRated?: boolean | null;
  /** "Top Brand" in customer-facing copy. */
  starBrand?: boolean | null;
}

export interface ToProductCardBadgeOptions {
  /**
   * Drop the badges that describe the partner rather than the product. Set it on a
   * listing scoped to one partner, where such a badge is on every card or none and so
   * carries nothing a customer can act on.
   *
   * Not for a surface that merely happens to draw several products from one partner: a
   * block showing several partners side by side is exactly where the badge does tell
   * them apart.
   */
  suppressPartnerBadges?: boolean;
}

/**
 * Ordered by priority: the card has a single badge slot, so the first rule that applies
 * takes it.
 *
 * Most Loved comes first by product decision, and the data supports it. On 598 prod
 * products, Top Brand is on 16% against Most Loved's 2%, so Most Loved is much the more
 * telling signal, and 43% of Most Loved products are also Top Brand, meaning the other
 * order would suppress nearly half of the Most Loved badges.
 */
const BADGE_RULES: {
  applies: (attributes: ProductCardBadgeAttributes) => boolean;
  badge: ProductCardBadge;
  /** Skipped under `suppressPartnerBadges`. */
  describesThePartner?: boolean;
}[] = [
  {
    applies: ({ highlyRated }) => Boolean(highlyRated),
    badge: { icon: 'mostLoved', label: 'Most Loved' },
  },
  {
    applies: ({ starBrand }) => Boolean(starBrand),
    badge: { icon: 'topBrand', label: 'Top Brand' },
    describesThePartner: true,
  },
];

/**
 * Resolves the one badge a card shows, so that every surface answers the question the
 * same way.
 *
 * Shared rather than written per surface because the ordering is a product decision
 * rather than a local one, and a surface that quietly reverses it shows a different
 * badge for the same product. It had been copied into six transformers before this
 * existed.
 */
export const toProductCardBadge = (
  attributes: ProductCardBadgeAttributes,
  { suppressPartnerBadges = false }: ToProductCardBadgeOptions = {},
): ProductCardBadge | undefined =>
  BADGE_RULES.find(
    ({ applies, describesThePartner }) =>
      !(describesThePartner && suppressPartnerBadges) && applies(attributes),
  )?.badge;
